import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { HrHierarchyTree } from '@/modules/hr/components/HrHierarchyTree';
import type { HrHierarchyNode, HrManagerTeamSummary } from '@/types/hr';
import { showApiError } from '@/utils/apiError';

export function HrMssPortalPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<HrManagerTeamSummary | null>(null);
  const [hierarchy, setHierarchy] = useState<HrHierarchyNode[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, tree] = await Promise.all([
        hrApi.mssSummary(),
        hrApi.hierarchyTree({ scope: 'MY_TEAM' }),
      ]);
      setSummary(s);
      setHierarchy(tree);
    } catch (err) {
      showApiError(err, 'Failed to load manager dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Manager Self Service"
        description="Your team hierarchy, pending approvals, and quick actions"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'MSS' }]}
        actions={
          <Button variant="secondary" onClick={() => load()}>
            Refresh
          </Button>
        }
      />
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {summary && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card p-4">
            <p className="text-sm text-slate-500">Direct reports</p>
            <p className="text-2xl font-semibold">{summary.directReports}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-slate-500">Pending leave</p>
            <p className="text-2xl font-semibold">{summary.pendingLeaves}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-slate-500">Pending attendance</p>
            <p className="text-2xl font-semibold">{summary.pendingAttendance}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-slate-500">My approval queue</p>
            <p className="text-2xl font-semibold">{summary.pendingApprovals}</p>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="card p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-semibold">My team hierarchy</h3>
            <span className="text-xs text-slate-500">Click an employee to open profile</span>
          </div>
          <HrHierarchyTree
            nodes={hierarchy}
            onSelect={(id) => navigate(`/hr/employees/${id}`)}
            defaultExpanded
          />
        </section>

        <aside className="card space-y-2 p-4">
          <h3 className="font-semibold">Quick actions</h3>
          <ButtonLink to="/work/approvals?ownerModule=HR&mineOnly=true" variant="secondary" className="w-full justify-center">
            My approvals
          </ButtonLink>
          {summary?.managerEmployeeId != null && (
            <ButtonLink
              to={`/hr/workforce?employeeId=${summary.managerEmployeeId}`}
              variant="secondary"
              className="w-full justify-center"
            >
              Team workforce
            </ButtonLink>
          )}
          <ButtonLink to="/hr/leaves" variant="secondary" className="w-full justify-center">
            Leave requests
          </ButtonLink>
          <ButtonLink to="/hr/attendance" variant="secondary" className="w-full justify-center">
            Attendance
          </ButtonLink>
          <ButtonLink to="/hr/employees" variant="secondary" className="w-full justify-center">
            All employees
          </ButtonLink>
        </aside>
      </div>
    </div>
  );
}
